/**
 * MaintenanceROIPanel Component
 * Industry-grade maintenance optimization and ROI visualization
 * Shows cost savings, efficiency gains, and maintenance strategy effectiveness
 */

import { useMemo, useState } from 'react'
import Plot from 'react-plotly.js'
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Clock,
  Wrench,
  Shield,
  Target,
  BarChart3,
  PieChart,
  Calendar,
  CheckCircle,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  Zap
} from 'lucide-react'
import clsx from 'clsx'

interface MaintenanceAction {
  id: string
  type: 'preventive' | 'predictive' | 'corrective' | 'condition-based'
  component: string
  scheduledDate: string
  estimatedCost: number
  actualCost?: number
  laborHours: number
  status: 'scheduled' | 'in-progress' | 'in_progress' | 'completed' | 'deferred'
  priority: 'low' | 'medium' | 'high' | 'critical'
  roi?: number
  failureRiskReduction?: number
}

interface CostBreakdown {
  category: string
  planned: number
  actual: number
  variance: number
  trend: 'up' | 'down' | 'stable'
}

interface EfficiencyMetric {
  name: string
  value: number
  target: number
  unit: string
  trend: number
  description: string
}

interface MaintenanceROIPanelProps {
  assetId: string
  period: string
  totalBudget: number
  totalSpent: number
  totalSavings: number
  actions: MaintenanceAction[]
  costBreakdown: CostBreakdown[]
  efficiencyMetrics: EfficiencyMetric[]
  projectedROI: number
  className?: string
}

export default function MaintenanceROIPanel({
  assetId,
  period,
  totalBudget,
  totalSpent,
  totalSavings,
  actions,
  costBreakdown,
  efficiencyMetrics,
  projectedROI,
  className
}: MaintenanceROIPanelProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'actions' | 'efficiency' | 'forecast'>('overview')

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const completedActions = actions.filter(a => a.status === 'completed')
    const preventiveActions = actions.filter(a => a.type === 'preventive' || a.type === 'predictive')
    const correctiveActions = actions.filter(a => a.type === 'corrective')

    const avgROI = completedActions.length > 0
      ? completedActions.reduce((sum, a) => sum + (a.roi || 0), 0) / completedActions.length
      : 0

    const totalLaborHours = actions.reduce((sum, a) => sum + a.laborHours, 0)
    const criticalActions = actions.filter(a => a.priority === 'critical' || a.priority === 'high')

    return {
      totalActions: actions.length,
      completedActions: completedActions.length,
      preventiveRatio: preventiveActions.length / actions.length,
      correctiveRatio: correctiveActions.length / actions.length,
      avgROI,
      totalLaborHours,
      criticalPending: criticalActions.filter(a => a.status !== 'completed').length,
      budgetUtilization: (totalSpent / totalBudget) * 100
    }
  }, [actions, totalBudget, totalSpent])

  // Cost breakdown for pie chart
  const costPieData = useMemo(() => {
    const byType = actions.reduce((acc, action) => {
      const type = action.type
      const cost = action.actualCost || action.estimatedCost
      acc[type] = (acc[type] || 0) + cost
      return acc
    }, {} as Record<string, number>)

    return {
      labels: Object.keys(byType).map(t => t.replace('-', ' ')),
      values: Object.values(byType),
      colors: ['#10b981', '#3b82f6', '#ef4444', '#f59e0b']
    }
  }, [actions])

  // Monthly trend data
  const monthlyTrendData = useMemo(() => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const planned = [45000, 38000, 52000, 41000, 35000, 48000, 42000, 55000, 39000, 44000, 50000, 46000]
    const actual = [42000, 41000, 48000, 38000, 32000, 51000, 39000, 52000, 36000, 42000, 47000, 43000]
    const savings = planned.map((p, i) => Math.max(0, p - actual[i]))

    return { months, planned, actual, savings }
  }, [])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-status-healthy/20 text-status-healthy border-status-healthy/30'
      case 'in-progress': return 'bg-primary-500/20 text-primary-400 border-primary-500/30'
      case 'scheduled': return 'bg-surface-700 text-surface-300 border-surface-600'
      case 'deferred': return 'bg-status-warning/20 text-status-warning border-status-warning/30'
      default: return 'bg-surface-700 text-surface-300 border-surface-600'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-status-critical'
      case 'high': return 'text-status-warning'
      case 'medium': return 'text-primary-400'
      default: return 'text-surface-400'
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'preventive': return <Shield size={14} className="text-status-healthy" />
      case 'predictive': return <Target size={14} className="text-primary-400" />
      case 'corrective': return <Wrench size={14} className="text-status-warning" />
      case 'condition-based': return <Zap size={14} className="text-purple-400" />
      default: return <Wrench size={14} />
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  return (
    <div className={clsx('rounded-xl border border-surface-700 bg-surface-900 overflow-hidden', className)}>
      {/* Header with Key Metrics */}
      <div className="p-4 bg-gradient-to-r from-green-500/10 via-primary-500/10 to-transparent border-b border-surface-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/20 text-green-500">
              <DollarSign size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-surface-100">Maintenance ROI Analysis</h3>
              <p className="text-xs text-surface-400">
                Asset: <span className="font-mono text-primary-400">{assetId}</span>
                <span className="mx-2">|</span>
                Period: {period}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={clsx(
              'px-3 py-1 rounded-full text-sm font-medium',
              projectedROI >= 150 ? 'bg-status-healthy/20 text-status-healthy' :
              projectedROI >= 100 ? 'bg-primary-500/20 text-primary-400' :
              'bg-status-warning/20 text-status-warning'
            )}>
              {projectedROI}% Projected ROI
            </span>
          </div>
        </div>

        {/* Key Metric Cards */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-surface-800/80 rounded-lg p-3 border border-surface-700">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-surface-400">Total Budget</span>
              <BarChart3 size={14} className="text-surface-500" />
            </div>
            <p className="text-xl font-bold text-surface-100 font-mono">{formatCurrency(totalBudget)}</p>
            <div className="mt-1 h-1.5 bg-surface-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full"
                style={{ width: `${Math.min(summaryStats.budgetUtilization, 100)}%` }}
              />
            </div>
            <p className="text-xs text-surface-500 mt-1">{summaryStats.budgetUtilization.toFixed(0)}% utilized</p>
          </div>

          <div className="bg-surface-800/80 rounded-lg p-3 border border-surface-700">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-surface-400">Total Spent</span>
              <TrendingDown size={14} className="text-status-healthy" />
            </div>
            <p className="text-xl font-bold text-surface-100 font-mono">{formatCurrency(totalSpent)}</p>
            <p className={clsx(
              'text-xs flex items-center gap-1 mt-1',
              totalSpent < totalBudget ? 'text-status-healthy' : 'text-status-warning'
            )}>
              {totalSpent < totalBudget ? <ArrowDownRight size={12} /> : <ArrowUpRight size={12} />}
              {formatCurrency(Math.abs(totalBudget - totalSpent))} {totalSpent < totalBudget ? 'under' : 'over'} budget
            </p>
          </div>

          <div className="bg-surface-800/80 rounded-lg p-3 border border-green-500/30">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-surface-400">Total Savings</span>
              <TrendingUp size={14} className="text-green-500" />
            </div>
            <p className="text-xl font-bold text-green-500 font-mono">{formatCurrency(totalSavings)}</p>
            <p className="text-xs text-surface-500 mt-1">
              From predictive maintenance
            </p>
          </div>

          <div className="bg-surface-800/80 rounded-lg p-3 border border-surface-700">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-surface-400">Avg ROI per Action</span>
              <Target size={14} className="text-primary-400" />
            </div>
            <p className="text-xl font-bold text-primary-400 font-mono">{summaryStats.avgROI.toFixed(0)}%</p>
            <p className="text-xs text-surface-500 mt-1">
              {summaryStats.completedActions} actions completed
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-surface-700">
        {[
          { id: 'overview', label: 'Overview', icon: PieChart },
          { id: 'actions', label: 'Actions', icon: Wrench },
          { id: 'efficiency', label: 'Efficiency', icon: Zap },
          { id: 'forecast', label: 'Forecast', icon: TrendingUp }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as typeof activeTab)}
            className={clsx(
              'flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors',
              activeTab === tab.id
                ? 'text-primary-400 border-b-2 border-primary-500 bg-primary-500/5'
                : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800/50'
            )}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-4">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-2 gap-6">
            {/* Cost Distribution Pie Chart */}
            <div>
              <h4 className="text-xs font-semibold text-surface-400 uppercase mb-3">Cost Distribution by Type</h4>
              <Plot
                data={[{
                  type: 'pie',
                  labels: costPieData.labels,
                  values: costPieData.values,
                  hole: 0.5,
                  marker: { colors: costPieData.colors },
                  textinfo: 'label+percent',
                  textposition: 'outside',
                  textfont: { size: 11, color: '#94a3b8' },
                  hovertemplate: '%{label}<br>%{value:$,.0f}<br>%{percent}<extra></extra>'
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any]}
                layout={{
                  height: 280,
                  margin: { t: 20, r: 20, b: 20, l: 20 },
                  paper_bgcolor: 'transparent',
                  plot_bgcolor: 'transparent',
                  font: { color: '#94a3b8' },
                  showlegend: false,
                  annotations: [{
                    text: `<b>${formatCurrency(totalSpent)}</b><br>Total`,
                    x: 0.5,
                    y: 0.5,
                    font: { size: 14, color: '#e2e8f0' },
                    showarrow: false
                  }]
                }}
                config={{ displayModeBar: false, responsive: true }}
                style={{ width: '100%' }}
              />
            </div>

            {/* Maintenance Strategy Breakdown */}
            <div>
              <h4 className="text-xs font-semibold text-surface-400 uppercase mb-3">Maintenance Strategy Mix</h4>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <div className="flex items-center gap-2">
                      <Shield size={14} className="text-status-healthy" />
                      <span className="text-surface-300">Preventive</span>
                    </div>
                    <span className="font-mono text-surface-200">{(summaryStats.preventiveRatio * 100).toFixed(0)}%</span>
                  </div>
                  <div className="h-2 bg-surface-700 rounded-full overflow-hidden">
                    <div className="h-full bg-status-healthy rounded-full" style={{ width: `${summaryStats.preventiveRatio * 100}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <div className="flex items-center gap-2">
                      <Wrench size={14} className="text-status-warning" />
                      <span className="text-surface-300">Corrective</span>
                    </div>
                    <span className="font-mono text-surface-200">{(summaryStats.correctiveRatio * 100).toFixed(0)}%</span>
                  </div>
                  <div className="h-2 bg-surface-700 rounded-full overflow-hidden">
                    <div className="h-full bg-status-warning rounded-full" style={{ width: `${summaryStats.correctiveRatio * 100}%` }} />
                  </div>
                </div>

                <div className="mt-6 p-3 bg-surface-800 rounded-lg">
                  <h5 className="text-xs font-medium text-surface-300 mb-2">Strategy Insights</h5>
                  <ul className="space-y-2 text-xs text-surface-400">
                    <li className="flex items-start gap-2">
                      <CheckCircle size={12} className="text-status-healthy mt-0.5" />
                      <span>Preventive ratio above industry benchmark of 65%</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <TrendingUp size={12} className="text-primary-400 mt-0.5" />
                      <span>Predictive maintenance adoption up 23% YoY</span>
                    </li>
                    {summaryStats.criticalPending > 0 && (
                      <li className="flex items-start gap-2">
                        <AlertTriangle size={12} className="text-status-warning mt-0.5" />
                        <span>{summaryStats.criticalPending} critical/high priority actions pending</span>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>

            {/* Cost Variance Table */}
            <div className="col-span-2">
              <h4 className="text-xs font-semibold text-surface-400 uppercase mb-3">Cost Breakdown by Category</h4>
              <div className="overflow-hidden rounded-lg border border-surface-700">
                <table className="w-full text-sm">
                  <thead className="bg-surface-800">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-surface-400">Category</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-surface-400">Planned</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-surface-400">Actual</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-surface-400">Variance</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-surface-400">Trend</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-700">
                    {costBreakdown.map((item) => (
                      <tr key={item.category} className="hover:bg-surface-800/50">
                        <td className="px-4 py-3 text-surface-200">{item.category}</td>
                        <td className="px-4 py-3 text-right font-mono text-surface-300">{formatCurrency(item.planned)}</td>
                        <td className="px-4 py-3 text-right font-mono text-surface-300">{formatCurrency(item.actual)}</td>
                        <td className={clsx(
                          'px-4 py-3 text-right font-mono',
                          item.variance < 0 ? 'text-status-healthy' : item.variance > 0 ? 'text-status-warning' : 'text-surface-400'
                        )}>
                          {item.variance > 0 ? '+' : ''}{formatCurrency(item.variance)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {item.trend === 'down' && <ArrowDownRight size={16} className="text-status-healthy inline" />}
                          {item.trend === 'up' && <ArrowUpRight size={16} className="text-status-warning inline" />}
                          {item.trend === 'stable' && <span className="text-surface-500">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Actions Tab */}
        {activeTab === 'actions' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold text-surface-400 uppercase">Maintenance Actions ({actions.length})</h4>
              <div className="flex items-center gap-2 text-xs">
                <span className="px-2 py-1 rounded bg-status-healthy/20 text-status-healthy">{actions.filter(a => a.status === 'completed').length} Completed</span>
                <span className="px-2 py-1 rounded bg-primary-500/20 text-primary-400">{actions.filter(a => a.status === 'in-progress').length} In Progress</span>
                <span className="px-2 py-1 rounded bg-surface-700 text-surface-300">{actions.filter(a => a.status === 'scheduled').length} Scheduled</span>
              </div>
            </div>

            <div className="space-y-2">
              {actions.map((action) => (
                <div
                  key={action.id}
                  className={clsx(
                    'p-3 rounded-lg border transition-all',
                    action.status === 'completed' ? 'border-status-healthy/30 bg-status-healthy/5' :
                    action.priority === 'critical' ? 'border-status-critical/30 bg-status-critical/5' :
                    'border-surface-700 bg-surface-800/50'
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {getTypeIcon(action.type)}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-surface-200">{action.component}</span>
                          <span className={clsx('px-1.5 py-0.5 text-xs rounded border', getStatusColor(action.status))}>
                            {action.status}
                          </span>
                          <span className={clsx('text-xs font-medium', getPriorityColor(action.priority))}>
                            {action.priority}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-surface-400">
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {action.scheduledDate}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {action.laborHours}h labor
                          </span>
                          <span className="capitalize">{action.type.replace('-', ' ')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-surface-200">
                        {formatCurrency(action.actualCost || action.estimatedCost)}
                      </p>
                      {action.roi && (
                        <p className={clsx(
                          'text-xs font-medium',
                          action.roi >= 100 ? 'text-status-healthy' : 'text-surface-400'
                        )}>
                          {action.roi}% ROI
                        </p>
                      )}
                    </div>
                  </div>

                  {action.failureRiskReduction && (
                    <div className="mt-2 pt-2 border-t border-surface-700/50">
                      <div className="flex items-center gap-2 text-xs">
                        <Shield size={12} className="text-status-healthy" />
                        <span className="text-surface-400">Failure risk reduced by</span>
                        <span className="font-mono text-status-healthy">{action.failureRiskReduction}%</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Efficiency Tab */}
        {activeTab === 'efficiency' && (
          <div className="space-y-6">
            <h4 className="text-xs font-semibold text-surface-400 uppercase">Efficiency Metrics</h4>
            <div className="grid grid-cols-2 gap-4">
              {efficiencyMetrics.map((metric) => {
                const percentage = (metric.value / metric.target) * 100
                const isGood = metric.trend >= 0

                return (
                  <div key={metric.name} className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-surface-200">{metric.name}</span>
                      <div className={clsx(
                        'flex items-center gap-1 text-xs font-medium',
                        isGood ? 'text-status-healthy' : 'text-status-warning'
                      )}>
                        {isGood ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                        {metric.trend > 0 ? '+' : ''}{metric.trend}%
                      </div>
                    </div>

                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-2xl font-bold text-surface-100 font-mono">{metric.value}</span>
                      <span className="text-sm text-surface-400">{metric.unit}</span>
                      <span className="text-xs text-surface-500">/ {metric.target} target</span>
                    </div>

                    <div className="h-2 bg-surface-700 rounded-full overflow-hidden mb-2">
                      <div
                        className={clsx(
                          'h-full rounded-full transition-all',
                          percentage >= 100 ? 'bg-status-healthy' :
                          percentage >= 80 ? 'bg-primary-500' :
                          percentage >= 60 ? 'bg-status-warning' : 'bg-status-critical'
                        )}
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>

                    <p className="text-xs text-surface-500">{metric.description}</p>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Forecast Tab */}
        {activeTab === 'forecast' && (
          <div className="space-y-4">
            <h4 className="text-xs font-semibold text-surface-400 uppercase">Cost Forecast & Trends</h4>
            <Plot
              data={[
                {
                  type: 'bar',
                  x: monthlyTrendData.months,
                  y: monthlyTrendData.planned,
                  name: 'Planned',
                  marker: { color: '#475569' }
                },
                {
                  type: 'bar',
                  x: monthlyTrendData.months,
                  y: monthlyTrendData.actual,
                  name: 'Actual',
                  marker: { color: '#3b82f6' }
                },
                {
                  type: 'scatter',
                  mode: 'lines+markers',
                  x: monthlyTrendData.months,
                  y: monthlyTrendData.savings,
                  name: 'Savings',
                  yaxis: 'y2',
                  line: { color: '#10b981', width: 2 },
                  marker: { size: 6 }
                }
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ] as any}
              layout={{
                height: 350,
                margin: { t: 30, r: 60, b: 40, l: 60 },
                paper_bgcolor: 'transparent',
                plot_bgcolor: 'transparent',
                font: { color: '#94a3b8', family: 'Inter, sans-serif' },
                barmode: 'group',
                xaxis: {
                  gridcolor: '#334155'
                },
                yaxis: {
                  title: { text: 'Cost ($)' },
                  gridcolor: '#334155',
                  tickformat: '$,.0f'
                },
                yaxis2: {
                  title: { text: 'Savings ($)' },
                  overlaying: 'y',
                  side: 'right',
                  tickformat: '$,.0f',
                  gridcolor: 'transparent'
                },
                legend: {
                  orientation: 'h',
                  y: -0.15,
                  font: { size: 11 }
                },
                hovermode: 'x unified'
              }}
              config={{ displayModeBar: false, responsive: true }}
              style={{ width: '100%' }}
            />

            {/* Forecast Summary */}
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
                <p className="text-xs text-surface-400 mb-1">Next Quarter Forecast</p>
                <p className="text-xl font-bold text-surface-100 font-mono">{formatCurrency(142000)}</p>
                <p className="text-xs text-status-healthy mt-1">↓ 8% vs current quarter</p>
              </div>
              <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
                <p className="text-xs text-surface-400 mb-1">Annual Projected Savings</p>
                <p className="text-xl font-bold text-green-500 font-mono">{formatCurrency(totalSavings * 4)}</p>
                <p className="text-xs text-surface-500 mt-1">Based on current trends</p>
              </div>
              <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
                <p className="text-xs text-surface-400 mb-1">5-Year ROI Projection</p>
                <p className="text-xl font-bold text-primary-400 font-mono">287%</p>
                <p className="text-xs text-surface-500 mt-1">With predictive maintenance</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
